/* SIZESOFTX
=========================================================================================
* Descripción: Menú de acciones del módulo de compras
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminaciones en submenú de otras acciones
* ---------------------------------------------------------------------------------------
*/

import { IconButton, Tooltip } from "@mui/material";
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from "@mui/icons-material/Add";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import { Fragment } from "react";
import { useSesion } from "../../../context/System/SesionContext";
import { useCompras } from "../../../context/Administracion/ComprasContext";
import { useDoubleClickPrevention } from '../../../hooks/useDoubleClickPrevention';
import MnuDangerActionsMenu from "../../utils/MnuDangerActionsMenu";

export default function MnuComprasActions(props) {
    const { onOpenBuscador, onImprimir, onAplicar, onEtiquetar, onOpenBuscadorCompra } = props;
    const { getPermisoBoton, getNombreBoton, getStatusPath, parametrosCajaForm } = useSesion();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();
    const {
        handleObtenerDatosCompra,
        handleCargarCompra,
        handleClickRegresarMainDashboard,
        listaPartidasCompra,
        handleConfirmDialog,
        titleActionSave,
        handleClickNuevaGuardarCompra,
        handleGuardarCompra,
        handleEditarCompra,
        modoLectura,
        statusCompra,
        folioCompra,
    } = useCompras();

    const tieneMovimientos = Array.isArray(listaPartidasCompra) && listaPartidasCompra.length > 0;

    const statusCabecera = String(statusCompra || "").trim().toUpperCase();
    const statusPartida = String(listaPartidasCompra[0]?.statuscompra || "").trim().toUpperCase();
    const statusMovimiento = ["TR", "CA"].includes(statusCabecera)
        ? statusCabecera
        : (["TR", "CA"].includes(statusPartida) ? statusPartida : statusCabecera || statusPartida);

    const movimientoBloqueado = statusMovimiento === "TR" || statusMovimiento === "CA";
    const compraAplicada = statusMovimiento === "TR";
    const guardarActivo = !modoLectura && !movimientoBloqueado;
    const puedeAplicar = tieneMovimientos && !movimientoBloqueado && modoLectura && titleActionSave === "NUEVA";
    const puedeRefrescar = !movimientoBloqueado && folioCompra && folioCompra > 0;

    const handleNueva = async () => {
        await handleClickNuevaGuardarCompra({ target: { innerText: "NUEVA" } });
    };

    const handleGuardar = async () => {
        await handleGuardarCompra();
    };

    const handleEditar = () => {
        handleEditarCompra();
    };

    const handleRefresh = () => {
        const pcTrabajo = parametrosCajaForm.caja?.trim().toUpperCase() || "";
        if (folioCompra && folioCompra > 0 && pcTrabajo) {
            handleCargarCompra(folioCompra, pcTrabajo);
        }
    };

    const handleEliminaPartida = () => {
        handleConfirmDialog("ELIMINAR PARTIDA");
    };

    const handleEliminaCompra = () => {
        handleConfirmDialog("ELIMINAR COMPRA");
    };

    return (
        <Fragment>
            <Tooltip title={"Salir (F4)"}>
                <IconButton onClick={handleClickRegresarMainDashboard}>
                    <HomeIcon className="icon" /><p className="icon">Salir</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={"Nueva (F4)"}>
                <span><IconButton onClick={() => executeWithProtection(handleNueva)} disabled={isProcessing}>
                    <AddIcon className="icon" /><p className="icon">Nuevo</p>
                </IconButton></span>
            </Tooltip>
            {guardarActivo && (
                <Tooltip title={"Guardar"}>
                    <span><IconButton onClick={() => executeWithProtection(handleGuardar)} disabled={isProcessing || !tieneMovimientos}>
                        <SaveIcon className="icon" /><p className="icon">Guardar</p>
                    </IconButton></span>
                </Tooltip>
            )}
            {modoLectura && tieneMovimientos && !movimientoBloqueado && (
                <Tooltip title={"Editar"}>
                    <span><IconButton onClick={() => executeWithProtection(handleEditar)} disabled={isProcessing}>
                        <EditIcon className="icon" /><p className="icon">Editar</p>
                    </IconButton></span>
                </Tooltip>
            )}
            <Tooltip title={"Aplicar (Alt+A)"} disabled={!puedeAplicar}>
                <span><IconButton onClick={() => executeWithProtection(onAplicar)} disabled={isProcessing || !puedeAplicar}>
                    <SaveAltIcon className="icon" /><p className="icon">Aplicar</p>
                </IconButton></span>
            </Tooltip>
            <Tooltip title={"Imprimir (Alt+I)"} disabled={!tieneMovimientos}>
                <span><IconButton onClick={() => executeWithProtection(onImprimir)} disabled={isProcessing || !tieneMovimientos}>
                    <PrintIcon className="icon" /><p className="icon">Imprimir</p>
                </IconButton></span>
            </Tooltip>
            <Tooltip title={"Etiquetas"} disabled={!tieneMovimientos || !compraAplicada}>
                <span><IconButton onClick={() => executeWithProtection(onEtiquetar)} disabled={isProcessing || !tieneMovimientos || !compraAplicada}>
                    <LocalOfferIcon className="icon" /><p className="icon">Etiquetas</p>
                </IconButton></span>
            </Tooltip>
            <Tooltip title={"Buscar Artículos (Alt+F1)"} disabled={movimientoBloqueado || modoLectura}>
                <span><IconButton onClick={() => onOpenBuscador ? onOpenBuscador() : null} disabled={movimientoBloqueado || modoLectura}>
                    <SearchIcon className="icon" /><p className="icon">Artículos</p>
                </IconButton></span>
            </Tooltip>
            <Tooltip title={"Buscar Compras"}>
                <IconButton onClick={onOpenBuscadorCompra}>
                    <SearchIcon className="icon" /><p className="icon">Compras</p>
                </IconButton>
            </Tooltip>
            <Tooltip title="Refrescar" disabled={!puedeRefrescar}>
                <span><IconButton onClick={handleRefresh} disabled={!puedeRefrescar}>
                    <RefreshIcon className="icon" />
                </IconButton></span>
            </Tooltip>
            <MnuDangerActionsMenu
                tooltip="Otras acciones"
                actions={[
                    {
                        label: "Eliminar partida",
                        onClick: handleEliminaPartida,
                        disabled: !tieneMovimientos || movimientoBloqueado || modoLectura,
                    },
                    {
                        label: "Eliminar compra",
                        onClick: handleEliminaCompra,
                        disabled: !tieneMovimientos || movimientoBloqueado || modoLectura,
                    },
                ]}
            />
        </Fragment>
    );
}